/**
 * Component List Service
 *
 * After login, the EVES component catalog is fetched and stored under COMPONENT_LIST_STORAGE_KEY
 * as a JSON array of strings ("component_name:version"). The UI resolves logical keys (e.g. OCPI)
 * against this list via getComponentValue / resolveComponentByLogicalKey.
 *
 * Enabled components for the signed-in user live on user_info.activeComponents (localStorage + Redux);
 * compare those with the resolved id using isActive / isActiveWithContext — not inside this module.
 */

import { fetchEVESComponents } from "@/services/tenants";

export const COMPONENT_LIST_STORAGE_KEY = "eves_component_list";
export const BASE_COMPONENT_LIST_STORAGE_KEY = "eves_base_component_list";

/** Persisted session user blob; includes activeComponents: string[] */
export const USER_INFO_STORAGE_KEY = "user_info";

/** Shape of API response items (tree or flat-normalized source) */
export interface ComponentListItem {
  component_name: string;
  component_version: string;
  is_base_component?: boolean;
  child?: ComponentListItem[];
}

/**
 * Match logical keys (ComponentKeys-style strings) to API display names (before ":version").
 * Order: first matching entry in the stored list wins.
 */
const LOGICAL_KEY_MATCHERS: Record<string, (baseName: string) => boolean> = {
  OCPI: (b) => b.toLowerCase().includes("ocpi"),
  OICP: (b) => b.toLowerCase().includes("oicp"),
  REFUND: (b) => b.toLowerCase().includes("refund"),
  PRICING: (b) =>
    b.toLowerCase().includes("pricing") && !b.toLowerCase().includes("tenant billing"),
  ORGANIZATION: (b) => b.toLowerCase().includes("organization"),
  STATISTICS: (b) =>
    b.toLowerCase().includes("analytics") || b.toLowerCase().includes("reports"),
  ANALYTICS: (b) =>
    b.toLowerCase().includes("analytics") || b.toLowerCase().includes("reports"),
  BILLING: (b) =>
    b.toLowerCase().includes("invoicing") ||
    b.toLowerCase().includes("processing fee"),
  TENANT_BILLING_PLATFORM: (b) => b.toLowerCase().includes("tenant billing"),
  ORG_BILLING_PLATFORM: (b) =>
    b.toLowerCase().includes("org/site billing") ||
    b.toLowerCase().includes("org billing"),
  ASSET: (b) => b.toLowerCase().includes("asset"),
  CAR: (b) => b.toLowerCase().includes("connector"),
  CAR_CONNECTOR: (b) => b.toLowerCase().includes("connector"),
  SCHEDULER: (b) => b.toLowerCase().includes("scheduler"),
  TELEMATICS: (b) => b.toLowerCase().includes("telematics"),
  OPERATIONAL_AND_MONITORING: (b) =>
    b.toLowerCase().includes("dash") && b.toLowerCase().includes("uptime"),
  LOG_ERRORS: (b) => b.toLowerCase().includes("error logs"),
  APPLICATION_LOGS: (b) =>
    b.toLowerCase().includes("application logs") || b.toLowerCase().includes("basic application"),
  GRIDIFY: (b) => b.toLowerCase().includes("gridify"),
  DEMAND_VIEW: (b) => b.toLowerCase().includes("demand view"),
  DEMAND_RESPONSE: (b) => b.toLowerCase().includes("demand response"),
  SMART_PROFILE: (b) => b.toLowerCase().includes("smart profile"),
  PHASE_BALANCING: (b) => b.toLowerCase().includes("phase balancing"),
  STATIC_LIMIT: (b) => b.toLowerCase().includes("static limit"),
  MICROGRID_CONTROLLER: (b) => b.toLowerCase().includes("microgrid"),
};

/**
 * Save component list to localStorage as flat string[].
 */
export const saveComponentList = (components: string[]): void => {
  try {
    const asStrings = components.filter((c): c is string => typeof c === "string");
    localStorage.setItem(COMPONENT_LIST_STORAGE_KEY, JSON.stringify(asStrings));
  } catch (error) {
    console.error("Failed to save component list to localStorage:", error);
  }
};

const parseStringArray = (parsed: unknown): string[] | null => {
  if (!Array.isArray(parsed) || parsed.length === 0) return null;
  const strings = parsed.filter((x): x is string => typeof x === "string");
  return strings.length > 0 ? strings : null;
};

/**
 * Get component list from localStorage as string[].
 * Handles migration from old ComponentListItem[] format.
 */
export const getComponentList = (): string[] | null => {
  try {
    const stored = localStorage.getItem(COMPONENT_LIST_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed) || parsed.length === 0) return null;
    if (typeof parsed[0] === "object" && parsed[0] !== null) {
      return flattenComponentItems(parsed as ComponentListItem[]);
    }
    return parseStringArray(parsed);
  } catch (error) {
    console.error("Failed to get component list from localStorage:", error);
    return null;
  }
};

/**
 * activeComponents from persisted user_info (same strings as catalog entries).
 */
export const getActiveComponentsFromStorage = (): string[] => {
  try {
    const stored = localStorage.getItem(USER_INFO_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as { activeComponents?: unknown };
    return parseStringArray(parsed?.activeComponents as unknown[]) ?? [];
  } catch {
    return [];
  }
};

/**
 * Map a logical key (e.g. OCPI, SMART_CHARGING) to one full "name:version" entry in the given list.
 */
export const resolveComponentByLogicalKey = (
  list: string[] | null | undefined,
  logicalKey: string
): string | null => {
  if (!list?.length) return null;

  const key = logicalKey.trim();
  const matcher = LOGICAL_KEY_MATCHERS[key.toUpperCase()];
  if (matcher) {
    const hit = list.find((full) => matcher(full.split(":")[0]));
    if (hit) return hit;
  }

  const keyLo = key.toLowerCase().replace(/_/g, "");
  const fuzzy = list.find((full) => {
    const base = full.split(":")[0].toLowerCase().replace(/[^a-z0-9]/g, "");
    return base.includes(keyLo) || keyLo.includes(base.slice(0, Math.min(12, base.length)));
  });
  return fuzzy ?? null;
};

/**
 * Resolve a logical key against the cached eves_component_list.
 */
export const getComponentValue = (logicalKey: string): string | null =>
  resolveComponentByLogicalKey(getComponentList(), logicalKey);

/**
 * Remove component list from localStorage.
 */
export const removeComponentList = (): void => {
  try {
    localStorage.removeItem(COMPONENT_LIST_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to remove component list from localStorage:", error);
  }
};

/**
 * Check if component list exists in localStorage.
 */
export const hasComponentList = (): boolean => {
  return localStorage.getItem(COMPONENT_LIST_STORAGE_KEY) !== null;
};

/**
 * Recursively collect "name:version" strings for items where is_base_component === true.
 * Traverses both parents and children.
 */
export const flattenBaseComponentItems = (items: ComponentListItem[]): string[] => {
  const names: string[] = [];
  items.forEach((item) => {
    if (item.is_base_component) {
      names.push(`${item.component_name}:${item.component_version}`);
    }
    if (item.child && item.child.length > 0) {
      names.push(...flattenBaseComponentItems(item.child));
    }
  });
  return names;
};

/**
 * Save base component list to localStorage as flat string[].
 */
export const saveBaseComponentList = (components: string[]): void => {
  try {
    const asStrings = components.filter((c): c is string => typeof c === "string");
    localStorage.setItem(BASE_COMPONENT_LIST_STORAGE_KEY, JSON.stringify(asStrings));
  } catch (error) {
    console.error("Failed to save base component list to localStorage:", error);
  }
};

/**
 * Get base component list from localStorage as string[].
 */
export const getBaseComponentList = (): string[] | null => {
  try {
    const stored = localStorage.getItem(BASE_COMPONENT_LIST_STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parseStringArray(parsed);
  } catch (error) {
    console.error("Failed to get base component list from localStorage:", error);
    return null;
  }
};

/**
 * Remove base component list from localStorage.
 */
export const removeBaseComponentList = (): void => {
  try {
    localStorage.removeItem(BASE_COMPONENT_LIST_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to remove base component list from localStorage:", error);
  }
};

/**
 * Flatten nested ComponentListItem tree to string[] of "name:version" entries.
 */
const flattenComponentItems = (items: ComponentListItem[]): string[] => {
  const names: string[] = [];
  items.forEach((item) => {
    names.push(`${item.component_name}:${item.component_version}`);
    if (item.child && item.child.length > 0) {
      names.push(...flattenComponentItems(item.child));
    }
  });
  return names;
};

/**
 * Check if the new component list differs from the currently stored one.
 * Returns true if there are additions, removals, or no prior list.
 */
export const hasPreviousListChanged = (newList: string[]): boolean => {
  const existing = getComponentList();
  if (!existing) return true;
  if (existing.length !== newList.length) return true;
  const existingSet = new Set(existing);
  return newList.some((c) => !existingSet.has(c));
};

/**
 * Normalize any API response shape to a flat string[].
 * Also extracts and saves base components as a side-effect.
 */
const normalizeComponentResponse = (response: unknown): string[] | null => {
  if (!response) return null;

  if (Array.isArray(response)) {
    if (response.length === 0) return null;
    if (typeof response[0] === "string") {
      return parseStringArray(response);
    }
  }

  let items: ComponentListItem[] | null = null;

  if (Array.isArray(response)) {
    items = response as ComponentListItem[];
  } else if (typeof response === "object" && response !== null) {
    const obj = response as Record<string, unknown>;
    for (const key of ["data", "components", "result", "items"]) {
      const val = obj[key];
      if (Array.isArray(val) && val.length > 0) {
        if (typeof val[0] === "string") {
          return parseStringArray(val);
        }
        items = val as ComponentListItem[];
        break;
      }
    }
    if (!items) {
      const arr = Object.values(obj).find(
        (v) => Array.isArray(v) && (v as unknown[]).length > 0
      );
      if (arr && typeof (arr as unknown[])[0] === "string") {
        return parseStringArray(arr as unknown[]);
      }
      items = arr ? (arr as ComponentListItem[]) : null;
    }
  }

  if (!items || items.length === 0) return null;
  saveBaseComponentList(flattenBaseComponentItems(items));
  return flattenComponentItems(items);
};

/**
 * Fetch component list from the EVES API and return as flat string[].
 * Returns null if the request fails or yields no data.
 */
export const fetchComponentsFromAPI = async (): Promise<string[] | null> => {
  try {
    const response = await fetchEVESComponents();
    return normalizeComponentResponse(response);
  } catch (err) {
    console.warn("EVES components API failed:", err);
  }
  return null;
};

/** React Query key for component list queries */
export const COMPONENT_LIST_QUERY_KEY = "ComponentList";

/** Custom event dispatched after login so HOCs can trigger a component fetch */
export const EVES_AUTH_LOGIN_EVENT = "eves:auth-login";

export const dispatchAuthLoginEvent = (): void => {
  window.dispatchEvent(new CustomEvent(EVES_AUTH_LOGIN_EVENT));
};

/**
 * Fetch component list, compare with previous, save to localStorage.
 * Returns true if the list changed (new fetch differs from what was stored).
 */
export const fetchAndSaveComponentList = async (): Promise<boolean> => {
  const components = await fetchComponentsFromAPI();
  if (!components || components.length === 0) {
    console.warn("No component list received from API");
    return false;
  }
  const changed = hasPreviousListChanged(components);
  saveComponentList(components);
  return changed;
};

/** In-flight singleton promise — prevents duplicate concurrent fetches */
let componentListFetchPromise: Promise<boolean> | null = null;

/**
 * Ensure component list is available: returns immediately if already cached,
 * otherwise fetches once (concurrent callers share the same request).
 * Returns true if the list was newly fetched and changed.
 */
export const ensureComponentListFetched = (): Promise<boolean> => {
  const existing = getComponentList();
  if (existing && existing.length > 0) {
    return Promise.resolve(false);
  }
  if (componentListFetchPromise) {
    return componentListFetchPromise;
  }
  componentListFetchPromise = fetchAndSaveComponentList().finally(() => {
    componentListFetchPromise = null;
  });
  return componentListFetchPromise;
};
